import * as React from 'react'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { useIsMd } from '@/hooks'
import { CommonInfo2 } from '@/icons'
import { cn } from '@/lib/utils'

import RenderIf from './render-if'

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot='tooltip-provider' delayDuration={delayDuration} {...props} />
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot='tooltip' {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot='tooltip-content'
        sideOffset={sideOffset}
        className={cn(
          'bg-dark-background-24 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-sm p-2 text-size-12/[140%] max-w-80',
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className='bg-dark-background-24 fill-dark-background-24 z-50 size-2.5 translate-y-[calc(-50%)] rotate-45 rounded-[2px] border-[1px] border-white-12 dark:border-0 border-t-0 border-l-0' />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}
interface TooltipInfoV2Props {
  content: React.ReactNode
  iconClassName?: string
  contentClassName?: string
  icon?: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'center' | 'end' | 'start'
  alignOffset?: number
}
export const TooltipInfo = ({
  content,
  iconClassName = 'size-5',
  contentClassName,
  icon,
  side,
  align,
  alignOffset,
}: TooltipInfoV2Props) => {
  const isMd = useIsMd()
  const [open, setOpen] = React.useState(false)
  const tooltipContent = (
    <>
      <TooltipTrigger
        asChild
        onClick={() => {
          if (isMd) return
          setOpen((pre) => !pre)
        }}
      >
        <button type='button'>{icon ? icon : <CommonInfo2 className={iconClassName} />}</button>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        alignOffset={alignOffset}
        className='border-[1px] border-white-12 dark:border-0'
      >
        <div className={contentClassName}>{content}</div>
      </TooltipContent>
    </>
  )
  if (isMd) return <Tooltip>{tooltipContent}</Tooltip>
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      {tooltipContent}
    </Tooltip>
  )
}

interface TooltipV2Props {
  content: React.ReactNode
  contentClassName?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'center' | 'end' | 'start'
  alignOffset?: number
  hideOnMobile?: boolean
}
export const TooltipV2: React.FC<React.PropsWithChildren<TooltipV2Props>> = ({
  content,
  contentClassName,
  side,
  align,
  alignOffset,
  children,
  hideOnMobile = false,
}) => {
  const isMd = useIsMd()
  const [open, setOpen] = React.useState(false)
  const tooltipContent = (
    <>
      <TooltipTrigger
        asChild
        onClick={() => {
          if (isMd || hideOnMobile) return
          setOpen((pre) => !pre)
        }}
      >
        {children}
      </TooltipTrigger>
      <RenderIf condition={!!content}>
        <TooltipContent
          side={side}
          align={align}
          alignOffset={alignOffset}
          className='border-[1px] border-white-12 dark:border-0'
        >
          <div className={contentClassName}>{content}</div>
        </TooltipContent>
      </RenderIf>
    </>
  )
  if (isMd) return <Tooltip>{tooltipContent}</Tooltip>
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      {tooltipContent}
    </Tooltip>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
